import Close from '@mui/icons-material/Close'
import CloudUpload from '@mui/icons-material/CloudUpload'
import PhotoLibrary from '@mui/icons-material/PhotoLibrary'
import CheckCircle from '@mui/icons-material/CheckCircle'
import ChevronLeft from '@mui/icons-material/ChevronLeft'
import ChevronRight from '@mui/icons-material/ChevronRight'
import DeleteOutline from '@mui/icons-material/DeleteOutline'
import AddPhotoAlternate from '@mui/icons-material/AddPhotoAlternate'
import { default as ErrorIcon } from '@mui/icons-material/Error'
import { default as ImageIcon } from '@mui/icons-material/Image'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Tab,
  Tabs,
  Typography,
  Chip,
  Snackbar,
  Grid,
  Card,
  CardMedia,
} from '@mui/material'
import { toast } from 'react-toastify'
import { useEffect, useState, useCallback, useMemo } from 'react'
import SendPrescriptionImages from 'src/clinic/components/ClinicManageBookingsPage/sendPrescriptionData/components/SendPrescriptionImages'
import { EVENT_TYPE_THERAPY_SESSION, EVENT_TYPE_SCREENING } from 'src/consumer/constants'
import { getDataWithParams, postDataWithParams } from 'src/consumer/services/profileService'
import { getSPDataWithParams, postSPDataWithParams } from 'src/supportPortal/services/services'
import { postMPDataWithParams } from 'src/mentor/services'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import { detectCurrentPortal } from 'src/shared/config/calendarReportApiConfig'
import PastPatientReportsDialog from 'src/clinic/components/ClinicManageBookingsPage/pastPatientReports/PastPatientReportsDialog'
import styled from 'styled-components'

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    border-radius: 20px;
    overflow: hidden;
    max-width: 900px;
    width: 100%;
  }
`

const DialogHeader = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  background: linear-gradient(135deg, rgba(67, 56, 202, 0.95) 0%, rgba(99, 102, 241, 0.95) 100%);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 4px 20px rgba(67, 56, 202, 0.2);
`

const DialogTitleText = styled(Typography)`
  font-size: 20px;
  font-weight: 700;
  color: white;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  display: flex;
  align-items: center;
  gap: 12px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`

const DialogContentContainer = styled(Box)`
  padding: 0;
  background: linear-gradient(135deg, rgba(250, 250, 255, 0.98) 0%, rgba(255, 255, 255, 0.98) 100%);
  min-height: 400px;
  max-height: 70vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(67, 56, 202, 0.05);
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(67, 56, 202, 0.2);
    border-radius: 4px;
    
    &:hover {
      background: rgba(67, 56, 202, 0.3);
    }
  }
`

const StyledTabs = styled(Tabs)`
  border-bottom: 2px solid rgba(67, 56, 202, 0.1);
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  
  .MuiTab-root {
    text-transform: none;
    font-weight: 600;
    font-size: 15px;
    min-height: 56px;
    color: rgba(67, 56, 202, 0.7);
    transition: all 0.2s ease;
    
    &:hover {
      color: rgba(67, 56, 202, 0.9);
      background: rgba(67, 56, 202, 0.05);
    }
    
    &.Mui-selected {
      color: rgba(67, 56, 202, 1);
      font-weight: 700;
    }
  }
  
  .MuiTabs-indicator {
    background: linear-gradient(90deg, rgba(67, 56, 202, 1) 0%, rgba(99, 102, 241, 1) 100%);
    height: 3px;
    border-radius: 3px 3px 0 0;
  }
`

const TabPanel = styled(Box)`
  padding: 24px;
  
  @media (max-width: 768px) {
    padding: 16px;
  }
`

const ImageGrid = styled(Grid)`
  margin-top: 16px;
`

const ImageCard = styled(Card)`
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border: 2px solid transparent;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(67, 56, 202, 0.15);
    border-color: rgba(67, 56, 202, 0.3);
  }
  
  &.selected {
    border-color: rgba(67, 56, 202, 0.6);
    box-shadow: 0 4px 16px rgba(67, 56, 202, 0.2);
  }
`

const ImageCardMedia = styled(CardMedia)`
  height: 200px;
  background: linear-gradient(135deg, rgba(67, 56, 202, 0.05) 0%, rgba(99, 102, 241, 0.05) 100%);
  position: relative;
  
  img {
    object-fit: cover;
    width: 100%;
    height: 100%;
  }
`

const ImageOverlay = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.6) 100%);
  display: flex;
  align-items: flex-end;
  padding: 12px;
  opacity: 0;
  transition: opacity 0.2s ease;
  
  ${ImageCard}:hover & {
    opacity: 1;
  }
`

const ImageActions = styled(Box)`
  display: flex;
  gap: 8px;
  width: 100%;
`

const ActionButton = styled(IconButton)`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  color: rgba(67, 56, 202, 0.9);
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 1);
    transform: scale(1.1);
  }
  
  &.delete {
    color: #ef4444;
    
    &:hover {
      background: rgba(239, 68, 68, 0.1);
    }
  }
`

const SelectedBadge = styled(Box)`
  position: absolute;
  top: 8px;
  right: 8px;
  background: linear-gradient(135deg, rgba(67, 56, 202, 0.95) 0%, rgba(99, 102, 241, 0.95) 100%);
  color: white;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(67, 56, 202, 0.3);
  z-index: 2;
`

const UploadArea = styled(Box)`
  border: 2px dashed rgba(67, 56, 202, 0.3);
  border-radius: 16px;
  padding: 40px;
  text-align: center;
  background: linear-gradient(135deg, rgba(67, 56, 202, 0.02) 0%, rgba(99, 102, 241, 0.02) 100%);
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    border-color: rgba(67, 56, 202, 0.5);
    background: linear-gradient(135deg, rgba(67, 56, 202, 0.05) 0%, rgba(99, 102, 241, 0.05) 100%);
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(67, 56, 202, 0.1);
  }
  
  &.drag-over {
    border-color: rgba(67, 56, 202, 0.8);
    background: linear-gradient(135deg, rgba(67, 56, 202, 0.1) 0%, rgba(99, 102, 241, 0.1) 100%);
    box-shadow: 0 8px 24px rgba(67, 56, 202, 0.2);
  }
`

const UploadIcon = styled(Box)`
  width: 80px;
  height: 80px;
  margin: 0 auto 16px;
  background: linear-gradient(135deg, rgba(67, 56, 202, 0.1) 0%, rgba(99, 102, 241, 0.1) 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(67, 56, 202, 0.8);
`

const EmptyState = styled(Box)`
  text-align: center;
  padding: 60px 20px;
  color: rgba(67, 56, 202, 0.6);
`

const EmptyStateIcon = styled(Box)`
  width: 120px;
  height: 120px;
  margin: 0 auto 24px;
  background: linear-gradient(135deg, rgba(67, 56, 202, 0.05) 0%, rgba(99, 102, 241, 0.05) 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(67, 56, 202, 0.4);
`

const FooterActions = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(10px);
  border-top: 1px solid rgba(67, 56, 202, 0.12);
  gap: 16px;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.04);
`

const SaveButton = styled(Button)`
  background: linear-gradient(135deg, #4338ca 0%, #6366f1 100%);
  color: #ffffff !important;
  font-weight: 700;
  font-size: 15px;
  text-transform: none;
  letter-spacing: 0.3px;
  padding: 12px 32px;
  border-radius: 12px;
  box-shadow: 0 4px 14px rgba(67, 56, 202, 0.4);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 160px;
  border: none;
  
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #3730a3 0%, #4f46e5 100%);
    box-shadow: 0 6px 20px rgba(67, 56, 202, 0.5);
    transform: translateY(-2px);
    color: #ffffff !important;
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(67, 56, 202, 0.4);
  }
  
  &:disabled {
    background: linear-gradient(135deg, rgba(67, 56, 202, 0.4) 0%, rgba(99, 102, 241, 0.4) 100%);
    color: rgba(255, 255, 255, 0.6) !important;
    box-shadow: none;
    cursor: not-allowed;
    transform: none;
  }
  
  .MuiButton-startIcon {
    margin-right: 8px;
    color: inherit;
  }
  
  .MuiCircularProgress-root {
    color: #ffffff !important;
  }
`

const CancelButton = styled(Button)`
  color: #64748b !important;
  font-weight: 600;
  font-size: 15px;
  text-transform: none;
  letter-spacing: 0.2px;
  padding: 12px 24px;
  border-radius: 12px;
  border: 1.5px solid rgba(100, 116, 139, 0.2);
  background: transparent;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: rgba(100, 116, 139, 0.08);
    border-color: rgba(100, 116, 139, 0.3);
    color: #475569 !important;
  }
  
  &:disabled {
    color: rgba(100, 116, 139, 0.4) !important;
    border-color: rgba(100, 116, 139, 0.1);
    cursor: not-allowed;
  }
`

const ImageCountChip = styled(Chip)`
  background: linear-gradient(135deg, rgba(67, 56, 202, 0.1) 0%, rgba(99, 102, 241, 0.1) 100%);
  color: rgba(67, 56, 202, 0.9);
  font-weight: 600;
  border: 1px solid rgba(67, 56, 202, 0.2);
`

const ErrorAlert = styled(Alert)`
  margin-bottom: 16px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.1);
`

const LoadingOverlay = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 12px;
`

// A4 Viewer Styled Components
const A4ViewerContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  background: linear-gradient(135deg, rgba(241, 245, 249, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%);
  border-radius: 12px;
  margin-bottom: 16px;
`

const A4ViewerHeader = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
`

const A4ViewerTitle = styled(Typography)`
  font-size: 14px;
  font-weight: 600;
  color: rgba(67, 56, 202, 0.9);
  display: flex;
  align-items: center;
  gap: 8px;
`

const A4PageContainer = styled(Box)`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 250px;
  max-height: 350px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`

const A4PageImage = styled('img')`
  max-width: 100%;
  max-height: 320px;
  object-fit: contain;
  border-radius: 4px;
`

const A4NavigationButton = styled(IconButton)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  color: rgba(67, 56, 202, 0.9);
  z-index: 5;
  
  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 1);
    box-shadow: 0 4px 12px rgba(67, 56, 202, 0.25);
  }
  
  &:disabled {
    background: rgba(255, 255, 255, 0.6);
    color: rgba(0, 0, 0, 0.26);
  }
  
  &.left {
    left: 16px;
  }
  
  &.right {
    right: 16px;
  }
`

const A4ThumbnailStrip = styled(Box)`
  display: flex;
  gap: 12px;
  overflow-x: auto;
  overflow-y: visible;
  padding: 12px 4px;
  
  &::-webkit-scrollbar {
    height: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(67, 56, 202, 0.05);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(67, 56, 202, 0.2);
    border-radius: 3px;
  }
`

const A4Thumbnail = styled(Box)`
  width: 60px;
  height: 80px;
  min-width: 60px;
  border-radius: 6px;
  overflow: hidden;
  border: 2px solid ${props => props.$active ? 'rgba(67, 56, 202, 0.8)' : 'rgba(67, 56, 202, 0.2)'};
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  
  &:hover {
    border-color: rgba(67, 56, 202, 0.5);
    transform: scale(1.05);
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const PageIndicator = styled(Typography)`
  font-size: 13px;
  color: rgba(67, 56, 202, 0.7);
  font-weight: 500;
  background: rgba(67, 56, 202, 0.08);
  padding: 4px 12px;
  border-radius: 12px;
`

const EmptyA4Viewer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  color: rgba(67, 56, 202, 0.5);
  text-align: center;
  gap: 12px;
`

const AddMoreButton = styled(Button)`
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.2) 100%);
  color: #10b981;
  font-weight: 600;
  font-size: 13px;
  text-transform: none;
  padding: 6px 16px;
  border-radius: 8px;
  border: 1px solid rgba(16, 185, 129, 0.3);
  
  &:hover {
    background: linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(16, 185, 129, 0.3) 100%);
    border-color: rgba(16, 185, 129, 0.5);
  }
`

const ThumbnailActionButton = styled(IconButton)`
  position: absolute;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(4px);
  padding: 3px;
  z-index: 10;
  width: 22px;
  height: 22px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  
  &:hover {
    transform: scale(1.1);
  }
  
  svg {
    font-size: 14px;
  }
  
  &.delete {
    top: -8px;
    right: -8px;
    background: rgba(239, 68, 68, 0.95);
    color: white;
    
    &:hover {
      background: rgba(239, 68, 68, 1);
    }
  }
  
  &.edit {
    bottom: -8px;
    right: -8px;
    background: rgba(67, 56, 202, 0.95);
    color: white;
    
    &:hover {
      background: rgba(67, 56, 202, 1);
    }
  }
`

const ThumbnailWrapper = styled(Box)`
  position: relative;
  flex-shrink: 0;
`

export default function EmrImagesUploadDialog({
  bookedEventInfo,
  closePopover,
  showGallery,
  mentorUserId,
  galleryImagesSearch,
  fileUploadApiSignature,
  showPatientHistoryInUploadPrescriptionWidget,
  patientHistoryGetApiSignature,
  dialogTitle = 'Upload Therapy Report Images',
  emrImagesGetApiSignature,
  emrImagesSaveApiSignature,
  open,
  onSuccess, // Callback when images are successfully saved
  initialImages = [], // Optional: Pre-populate with existing images (array of image IDs)
  initialSignedUrls = [], // Optional: Pre-populate with signed URLs
}) {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [successMessage, setSuccessMessage] = useState(null)
  const [emrReportImages, setEmrReportImages] = useState([])
  const [tabValue, setTabValue] = useState(0)
  const [signedUrls, setSignedUrls] = useState([])
  const [isUploading, setIsUploading] = useState(false)
  const [pendingSaveImages, setPendingSaveImages] = useState([])
  const [autoSaveInProgress, setAutoSaveInProgress] = useState(false)
  const [currentViewIndex, setCurrentViewIndex] = useState(0) // For A4 viewer pagination

  // Determine event type and dialog title
  const eventType = bookedEventInfo?.eventType
  const isTherapySession = eventType === EVENT_TYPE_THERAPY_SESSION
  const isScreening = eventType === EVENT_TYPE_SCREENING
  const isAssessment = eventType === 'assessment' || eventType === 'assessment-report'
  const isInvestigation = eventType === 'investigation' || eventType === 'investigation-report'
  const isPrescription = eventType === 'prescription' || eventType === 'consultation'
  const isSupportedEventType = isTherapySession || isScreening || isAssessment || isInvestigation || isPrescription
  
  const displayTitle = dialogTitle || (isTherapySession 
    ? 'Upload Therapy Report Images' 
    : isScreening 
    ? 'Upload Screening Report Images'
    : isAssessment
    ? 'Upload Assessment Report Images'
    : isInvestigation
    ? 'Upload Investigation Report Images'
    : isPrescription
    ? 'Upload Prescription Documents'
    : 'Upload EMR Report Images')

  useEffect(() => {
    if (open && bookedEventInfo?.id && isSupportedEventType) {
      // First, set initial images if provided (for immediate display)
      if (initialImages?.length > 0 || initialSignedUrls?.length > 0) {
        console.log('[EmrImagesUploadDialog] Setting initial images from props:', { initialImages, initialSignedUrls })
        
        // Set image IDs
        if (initialImages?.length > 0) {
          setEmrReportImages(initialImages)
        }
        
        // Determine URLs to display
        if (initialSignedUrls?.length > 0) {
          // Use signed URLs directly (they may already be full URLs or need prefix)
          const urls = initialSignedUrls.map(url => {
            if (!url) return ''
            if (url.startsWith('http://') || url.startsWith('https://')) return url
            return `https://media.aumhum.com/images/prod/${url}`
          })
          setSignedUrls(urls)
        } else if (initialImages?.length > 0) {
          // Generate URLs from image IDs (uploadedImages)
          const generatedUrls = initialImages.map(id => {
            if (!id) return ''
            if (id.startsWith('http://') || id.startsWith('https://')) return id
            return `https://media.aumhum.com/images/prod/${id}`
          })
          setSignedUrls(generatedUrls)
        }
      }
      // Then fetch from API (will update/override if API has newer data)
      fetchEmrReportImages()
    } else if (open) {
      // Reset state when dialog opens but event type is not supported
      setEmrReportImages([])
      setSignedUrls([])
      setError(null)
      setSuccessMessage(null)
    } else {
      // Reset state when dialog closes
      setEmrReportImages([])
      setSignedUrls([])
      setError(null)
      setSuccessMessage(null)
      setCurrentViewIndex(0) // Reset viewer index
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bookedEventInfo?.id, eventType])

  const fetchEmrReportImages = async () => {
    if (!bookedEventInfo?.id || !isSupportedEventType) {
      setError('Unsupported event type for EMR report images')
      return
    }

    if (!emrImagesGetApiSignature) {
      setError('API signature not provided for fetching EMR report images')
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Use appropriate API service based on portal
      const currentPortal = detectCurrentPortal()
      const isSupportPortal = currentPortal === 'support'
      
      const resp = isSupportPortal
        ? await getSPDataWithParams(emrImagesGetApiSignature, {
            eventId: bookedEventInfo.id,
          })
        : await getDataWithParams(emrImagesGetApiSignature, {
            eventId: bookedEventInfo.id,
          })

      if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
        const data = getDataMapFromCommonResponse(resp)
        console.log('[EmrImagesUploadDialog] fetchEmrReportImages API response data:', data)
        
        // Handle multiple response field names for different report types
        // Priority: reportImages > prescriptionImages > uploadedImages > fileIds
        const imageIds = data?.reportImages ?? data?.prescriptionImages ?? data?.uploadedImages ?? data?.fileIds ?? []
        
        // For signed URLs: signedURLs > signedUrls > uploadedSignedImages
        const signedUrlsFromApi = data?.signedURLs ?? data?.signedUrls ?? data?.uploadedSignedImages ?? []
        
        // For unsigned images from uploadedImages (need URL prefix)
        const unsignedImages = data?.uploadedImages ?? []
        
        console.log('[EmrImagesUploadDialog] Extracted:', { 
          imageIds, 
          signedUrlsFromApi, 
          unsignedImages 
        })
        
        // Determine final URLs to display
        let finalUrls = []
        
        // If we have signed URLs (uploadedSignedImages), use them directly
        if (signedUrlsFromApi.length > 0) {
          finalUrls = signedUrlsFromApi.map(url => {
            if (!url) return ''
            // If already a full URL, use as is
            if (url.startsWith('http://') || url.startsWith('https://')) return url
            // Otherwise add the media prefix
            return `https://media.aumhum.com/images/prod/${url}`
          })
        } 
        // Otherwise, generate URLs from image IDs (uploadedImages or other fields)
        else if (imageIds.length > 0) {
          finalUrls = imageIds.map(id => {
            if (!id) return ''
            if (id.startsWith('http://') || id.startsWith('https://')) return id
            return `https://media.aumhum.com/images/prod/${id}`
          })
        }
        
        console.log('[EmrImagesUploadDialog] Setting state with imageIds:', imageIds, 'signedUrls:', finalUrls)
        setEmrReportImages(imageIds)
        setSignedUrls(finalUrls)
      } else {
        console.error('[EmrImagesUploadDialog] API error:', resp?.data?.message)
        setError(resp?.data?.message || 'Failed to load existing images')
      }
    } catch (err) {
      console.error('Error fetching EMR images:', err)
      setError('An unexpected error occurred while loading images')
    } finally {
      setLoading(false)
    }
  }

  const saveEmrReportImages = async () => {
    if (!bookedEventInfo?.id || !isSupportedEventType) {
      setError('Unsupported event type for EMR report images')
      return
    }

    if (!emrImagesSaveApiSignature) {
      setError('API signature not provided for saving EMR report images')
      return
    }

    if (!emrReportImages || emrReportImages.length === 0) {
      setError('Please add at least one image before saving')
      return
    }

    setSaving(true)
    setError(null)
    setSuccessMessage(null)

    try {
      // Use appropriate API service based on portal
      const currentPortal = detectCurrentPortal()
      const isSupportPortal = currentPortal === 'support'
      const isMentorPortal = currentPortal === 'mentor'
      
      const resp = isSupportPortal
        ? await postSPDataWithParams(
            emrImagesSaveApiSignature,
            emrReportImages,
            { eventId: bookedEventInfo.id }
          )
        : isMentorPortal
        ? await postMPDataWithParams(
            emrImagesSaveApiSignature,
            { reportImages: emrReportImages, eventId: bookedEventInfo.id }
          )
        : await postDataWithParams(
            emrImagesSaveApiSignature,
            emrReportImages,
            { eventId: bookedEventInfo.id }
          )

      if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
        const message = resp?.data?.message || 'Images saved successfully!'
        setSuccessMessage(message)
        toast.success(message)
        // Call onSuccess callback with saved image IDs
        if (onSuccess) {
          onSuccess(emrReportImages)
        }
        setTimeout(() => {
          closePopover()
        }, 1500)
      } else {
        const errorMsg = resp?.data?.message || 'Failed to save images'
        setError(errorMsg)
        toast.error(errorMsg)
      }
    } catch (err) {
      console.error('Error saving EMR images:', err)
      const errorMsg = err?.message || 'Network error. Please check your connection and try again.'
      setError(errorMsg)
      toast.error(errorMsg)
    } finally {
      setSaving(false)
    }
  }

  const handleAddImage = useCallback((imageId) => {
    if (!imageId) {
      setError('Invalid image. Please try uploading again.')
      return
    }

    // Check for duplicates
    if (emrReportImages.includes(imageId)) {
      toast.warning('This image is already added')
      return
    }

    setEmrReportImages((prev) => [...prev, imageId])
    setError(null)
    toast.success('Image added successfully')
  }, [emrReportImages])

  const handleDeleteImage = useCallback((imageId) => {
    setEmrReportImages((prev) => prev.filter((id) => id !== imageId))
    toast.info('Image removed')
  }, [])

  // Handle uploading state changes
  const handleUploadingChange = useCallback((uploading) => {
    setIsUploading(uploading)
  }, [])

  // Auto-save when upload completes
  const handleUploadComplete = useCallback(async (imageId) => {
    if (!emrImagesSaveApiSignature || !bookedEventInfo?.id) {
      return
    }

    // Add to pending save queue
    setPendingSaveImages((prev) => [...prev, imageId])
  }, [emrImagesSaveApiSignature, bookedEventInfo?.id])

  // Process pending saves with debounce
  useEffect(() => {
    if (pendingSaveImages.length === 0 || autoSaveInProgress || isUploading) {
      return
    }

    // Debounce auto-save to batch multiple quick uploads
    const timer = setTimeout(async () => {
      if (emrReportImages.length === 0) {
        return
      }
      
      setAutoSaveInProgress(true)

      try {
        const currentPortal = detectCurrentPortal()
        const isSupportPortal = currentPortal === 'support'
        const isMentorPortal = currentPortal === 'mentor'
        
        const resp = isSupportPortal
          ? await postSPDataWithParams(
              emrImagesSaveApiSignature,
              emrReportImages,
              { eventId: bookedEventInfo.id }
            )
          : isMentorPortal
          ? await postMPDataWithParams(
              emrImagesSaveApiSignature,
              { reportImages: emrReportImages, eventId: bookedEventInfo.id }
            )
          : await postDataWithParams(
              emrImagesSaveApiSignature,
              emrReportImages,
              { eventId: bookedEventInfo.id }
            )

        if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
          setPendingSaveImages([])
        } else {
          // Always clear pending saves to prevent infinite retry loop
          setPendingSaveImages([])
        }
      } catch (err) {
        // Always clear pending saves to prevent infinite retry loop on error
        setPendingSaveImages([])
      } finally {
        setAutoSaveInProgress(false)
      }
    }, 1500) // Wait 1.5 seconds after last upload to batch saves

    return () => clearTimeout(timer)
  }, [pendingSaveImages, emrReportImages, isUploading, autoSaveInProgress, emrImagesSaveApiSignature, bookedEventInfo?.id])

  const hasImages = emrReportImages && emrReportImages.length > 0
  const hasSignedUrls = signedUrls && signedUrls.length > 0

  // Helper function to convert image ID to media URL
  const getMediaUrl = useCallback((imageId) => {
    if (!imageId) return ''
    if (imageId.startsWith('http://') || imageId.startsWith('https://')) {
      return imageId
    }
    return `https://media.aumhum.com/images/prod/${imageId}`
  }, [])

  // Handle A4 viewer navigation
  const handlePreviousPage = useCallback(() => {
    setCurrentViewIndex(prev => Math.max(0, prev - 1))
  }, [])

  const handleNextPage = useCallback(() => {
    setCurrentViewIndex(prev => Math.min((signedUrls.length || emrReportImages.length) - 1, prev + 1))
  }, [signedUrls.length, emrReportImages.length])

  // Handle removing an image
  const handleRemoveImage = useCallback((indexToRemove) => {
    setEmrReportImages(prev => prev.filter((_, index) => index !== indexToRemove))
    setSignedUrls(prev => prev.filter((_, index) => index !== indexToRemove))
    // Adjust current view index if needed
    if (currentViewIndex >= emrReportImages.length - 1 && currentViewIndex > 0) {
      setCurrentViewIndex(prev => prev - 1)
    }
    toast.info('Image removed from list')
  }, [currentViewIndex, emrReportImages.length])

  // Get the current display URLs (either from signedUrls or generate from image IDs)
  const displayUrls = useMemo(() => {
    if (signedUrls && signedUrls.length > 0) {
      return signedUrls
    }
    if (emrReportImages && emrReportImages.length > 0) {
      return emrReportImages.map(id => getMediaUrl(id))
    }
    return []
  }, [signedUrls, emrReportImages, getMediaUrl])


  return (
    <>
      <StyledDialog
        open={open}
        onClose={closePopover}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            overflow: 'hidden',
          },
        }}
      >
        <DialogHeader>
          <DialogTitleText>
            <ImageIcon sx={{ fontSize: 24 }} />
            {displayTitle}
          </DialogTitleText>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {hasImages && (
              <ImageCountChip
                label={`${emrReportImages.length} ${emrReportImages.length === 1 ? 'Image' : 'Images'}`}
                size="small"
              />
            )}
            <IconButton
              onClick={closePopover}
              sx={{
                color: 'white',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                },
              }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogHeader>

        <DialogContentContainer>
          {error && (
            <Box sx={{ p: 2 }}>
              <ErrorAlert severity="error" icon={<ErrorIcon />}>
                {error}
              </ErrorAlert>
            </Box>
          )}

          {/* Add New Images Section Header - AT THE TOP */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            pb: 1,
            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(16, 185, 129, 0.1) 100%)',
            borderBottom: displayUrls.length > 0 ? '1px solid rgba(16, 185, 129, 0.15)' : 'none',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AddPhotoAlternate sx={{ fontSize: 22, color: '#10b981' }} />
              <Typography sx={{ 
                fontSize: 15, 
                fontWeight: 600,
                color: '#10b981',
              }}>
                {displayUrls.length > 0 ? 'Add More Images' : 'Upload Images'}
              </Typography>
            </Box>
            <Typography sx={{ 
              fontSize: 12, 
              color: 'rgba(16, 185, 129, 0.7)',
            }}>
              Click the + button to add
            </Typography>
          </Box>

          {showGallery ? (
            <>
              <StyledTabs
                value={tabValue}
                onChange={(e, newValue) => setTabValue(newValue)}
                variant="fullWidth"
              >
                <Tab
                  icon={<CloudUpload sx={{ fontSize: 20, mb: 0.5 }} />}
                  iconPosition="start"
                  label="Upload New"
                />
                <Tab
                  icon={<PhotoLibrary sx={{ fontSize: 20, mb: 0.5 }} />}
                  iconPosition="start"
                  label="Choose from Gallery"
                />
              </StyledTabs>

              <TabPanel role="tabpanel" hidden={tabValue !== 0}>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                    <CircularProgress size={40} sx={{ color: 'rgba(67, 56, 202, 0.7)' }} />
                  </Box>
                ) : (
                  <Box>
                    <SendPrescriptionImages
                      bookedEventInfo={bookedEventInfo}
                      setUploadPrescriptionImages={setEmrReportImages}
                      uploadPrescriptionImages={emrReportImages}
                      loading={loading}
                      setLoading={setLoading}
                      showGallery={false}
                      mentorUserId={mentorUserId}
                      galleryImagesSearch={galleryImagesSearch}
                      fileUploadApiSignature={fileUploadApiSignature}
                      onUploadingChange={handleUploadingChange}
                      onUploadComplete={handleUploadComplete}
                    />
                  </Box>
                )}
              </TabPanel>

              <TabPanel role="tabpanel" hidden={tabValue !== 1}>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                    <CircularProgress size={40} sx={{ color: 'rgba(67, 56, 202, 0.7)' }} />
                  </Box>
                ) : (
                  <SendPrescriptionImages
                    bookedEventInfo={bookedEventInfo}
                    setUploadPrescriptionImages={setEmrReportImages}
                    uploadPrescriptionImages={emrReportImages}
                    loading={loading}
                    setLoading={setLoading}
                    showGallery={true}
                    mentorUserId={mentorUserId}
                    galleryImagesSearch={galleryImagesSearch}
                    fileUploadApiSignature={fileUploadApiSignature}
                    onUploadingChange={handleUploadingChange}
                    onUploadComplete={handleUploadComplete}
                  />
                )}
              </TabPanel>
            </>
          ) : (
            <TabPanel>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                  <CircularProgress size={40} sx={{ color: 'rgba(67, 56, 202, 0.7)' }} />
                </Box>
              ) : (
                <SendPrescriptionImages
                  bookedEventInfo={bookedEventInfo}
                  setUploadPrescriptionImages={setEmrReportImages}
                  uploadPrescriptionImages={emrReportImages}
                  loading={loading}
                  setLoading={setLoading}
                  showGallery={false}
                  mentorUserId={mentorUserId}
                  galleryImagesSearch={galleryImagesSearch}
                  fileUploadApiSignature={fileUploadApiSignature}
                  onUploadingChange={handleUploadingChange}
                  onUploadComplete={handleUploadComplete}
                />
              )}
            </TabPanel>
          )}

          {/* A4 Document Viewer - Shows existing/uploaded images (AFTER upload section) */}
          {displayUrls.length > 0 && (
            <A4ViewerContainer>
              <A4ViewerHeader>
                <A4ViewerTitle>
                  <PhotoLibrary sx={{ fontSize: 18 }} />
                  Uploaded Documents ({displayUrls.length})
                </A4ViewerTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <PageIndicator>
                    {currentViewIndex + 1} of {displayUrls.length}
                  </PageIndicator>
                </Box>
              </A4ViewerHeader>

              <A4PageContainer>
                <A4NavigationButton
                  className="left"
                  onClick={handlePreviousPage}
                  disabled={currentViewIndex === 0}
                >
                  <ChevronLeft />
                </A4NavigationButton>

                <A4PageImage
                  src={displayUrls[currentViewIndex]}
                  alt={`Document ${currentViewIndex + 1}`}
                  onError={(e) => {
                    console.error('A4 Viewer: Image load error', { url: displayUrls[currentViewIndex], index: currentViewIndex })
                    e.target.style.display = 'none'
                  }}
                />

                <A4NavigationButton
                  className="right"
                  onClick={handleNextPage}
                  disabled={currentViewIndex === displayUrls.length - 1}
                >
                  <ChevronRight />
                </A4NavigationButton>

                {/* Delete button on current image */}
                <IconButton
                  onClick={() => handleRemoveImage(currentViewIndex)}
                  sx={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    background: 'rgba(239, 68, 68, 0.95)',
                    color: 'white',
                    boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)',
                    '&:hover': {
                      background: 'rgba(239, 68, 68, 1)',
                      transform: 'scale(1.05)',
                    },
                  }}
                >
                  <DeleteOutline />
                </IconButton>
              </A4PageContainer>

              {/* Thumbnail Strip */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <A4ThumbnailStrip sx={{ flex: 1 }}>
                  {displayUrls.map((url, index) => (
                    <ThumbnailWrapper key={index}>
                      <A4Thumbnail
                        $active={index === currentViewIndex}
                        onClick={() => setCurrentViewIndex(index)}
                      >
                        <img
                          src={url}
                          alt={`Thumbnail ${index + 1}`}
                          onError={(e) => {
                            e.target.style.display = 'none'
                          }}
                        />
                      </A4Thumbnail>
                      <ThumbnailActionButton
                        className="delete"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleRemoveImage(index)
                        }}
                      >
                        <DeleteOutline />
                      </ThumbnailActionButton>
                    </ThumbnailWrapper>
                  ))}
                </A4ThumbnailStrip>
              </Box>
            </A4ViewerContainer>
          )}

          {showPatientHistoryInUploadPrescriptionWidget && (
            <Box
              sx={{
                borderTop: '1px solid rgba(67, 56, 202, 0.1)',
                maxHeight: '400px',
                overflowY: 'auto',
                p: 2,
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 2, color: 'rgba(67, 56, 202, 0.8)', fontWeight: 600 }}>
                Patient History
              </Typography>
              <PastPatientReportsDialog
                patientInfo={{
                  ...(bookedEventInfo?.patientInfo || {}),
                  id: bookedEventInfo?.patientId,
                }}
                forceShowAllReports
                patientHistoryGetApiSignature={patientHistoryGetApiSignature}
              />
            </Box>
          )}
        </DialogContentContainer>

        <FooterActions>
          {hasImages && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {autoSaveInProgress || isUploading ? (
                <>
                  <CircularProgress size={14} sx={{ color: 'rgba(67, 56, 202, 0.6)' }} />
                  <Typography sx={{ fontSize: 13, color: 'rgba(67, 56, 202, 0.7)', fontWeight: 500 }}>
                    {isUploading ? 'Uploading…' : 'Auto-saving…'}
                  </Typography>
                </>
              ) : (
                <>
                  <CheckCircle sx={{ fontSize: 16, color: '#10b981' }} />
                  <Typography sx={{ fontSize: 13, color: '#10b981', fontWeight: 500 }}>
                    {emrReportImages.length} {emrReportImages.length === 1 ? 'image' : 'images'} saved
                  </Typography>
                </>
              )}
            </Box>
          )}
          <Box sx={{ flex: 1 }} />
          <SaveButton
            onClick={closePopover}
            disabled={isUploading}
            startIcon={isUploading ? <CircularProgress size={16} color="inherit" /> : autoSaveInProgress ? <CircularProgress size={16} color="inherit" /> : <CheckCircle />}
          >
            {isUploading ? 'Uploading…' : autoSaveInProgress ? 'Saving… (click to close)' : 'Done'}
          </SaveButton>
        </FooterActions>
      </StyledDialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ borderRadius: 2 }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </>
  )
}
